import api from '../../../services/api.service'

/**
 * Guest Journey: multi-step forms a guest fills after identifying with their
 * booking. Answers can order guest features (upsell) through the existing flow.
 */
export const journeyModule = {
    namespaced: true,
    state: {
        journeys: [],
        loaded: false,
        unread: 0,
    },
    getters: {
        getJourneys(state) {
            return state.journeys || [];
        },
        getJourneysMap(state) {
            return (state.journeys || []).toObject('id');
        },
        totalUnread(state) {
            return state.unread > 0 ? state.unread : '';
        },
        canRead: (state, getters, rootState, rootGetters) => rootGetters['user/canRead']('journey'),
        canEdit: (state, getters, rootState, rootGetters) => rootGetters['user/canEdit']('journey'),
    },
    mutations: {
        setJourneys(state, journeys) {
            state.journeys = journeys || [];
            state.loaded = true;
        },
        setUnread(state, total) {
            state.unread = total || 0;
        },
    },
    actions: {
        getAll({commit, dispatch}, {page, amount, filter, sortby, sortcolumn} = {}) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/get', {
                    page: page || 1,
                    amount: amount || 200,
                    sortby: sortby,
                    sortcolumn: sortcolumn
                }, filter || {})
                    .then(
                        (ans) => {
                            commit('setJourneys', ans['Journeys'] || []);
                            resolve(ans);
                        }, (error) => {
                            dispatch('notifications/error', {
                                title: 'Journey',
                                message: (typeof error === 'object' && error.message !== undefined ? error.message : error)
                            }, {root: true});
                            reject(error.message);
                        });
            });
        },
        get(actions, id) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/id/' + id)
                    .then((ans) => resolve(ans['Journey']), (error) => reject(error.message));
            });
        },
        add({dispatch}, journey) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/add', null, journey)
                    .then(
                        (ans) => {
                            dispatch('notifications/success',
                                {title: 'Journey', message: 'Journey created successfully'}, {root: true});
                            resolve(ans['Journey']);
                        }, (error) => {
                            dispatch('notifications/error', {
                                title: 'Journey',
                                message: (typeof error === 'object' && error.message !== undefined ? error.message : error)
                            }, {root: true});
                            reject(error.message);
                        });
            });
        },
        update({dispatch}, journey) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/update', null, journey)
                    .then(
                        (ans) => {
                            dispatch('notifications/success',
                                {title: 'Journey', message: 'Journey saved successfully'}, {root: true});
                            resolve(ans['Journey']);
                        }, (error) => {
                            dispatch('notifications/error', {
                                title: 'Journey',
                                message: (typeof error === 'object' && error.message !== undefined ? error.message : error)
                            }, {root: true});
                            reject(error.message);
                        });
            });
        },
        toggle({dispatch}, id) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/toggle/' + id)
                    .then((ans) => resolve(ans['Journey']), (error) => {
                        dispatch('notifications/error', {
                            title: 'Journey',
                            message: (typeof error === 'object' && error.message !== undefined ? error.message : error)
                        }, {root: true});
                        reject(error.message);
                    });
            });
        },
        duplicate({dispatch}, id) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/duplicate/' + id)
                    .then(
                        (ans) => {
                            dispatch('notifications/success',
                                {title: 'Journey', message: 'Journey duplicated'}, {root: true});
                            resolve(ans['Journey']);
                        }, (error) => reject(error.message));
            });
        },
        remove({dispatch}, id) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/delete/' + id)
                    .then(
                        (ans) => {
                            dispatch('notifications/success',
                                {title: 'Journey', message: 'Journey deleted'}, {root: true});
                            resolve(ans);
                        }, (error) => {
                            dispatch('notifications/error', {
                                title: 'Journey',
                                message: (typeof error === 'object' && error.message !== undefined ? error.message : error)
                            }, {root: true});
                            reject(error.message);
                        });
            });
        },
        getConflicts(actions, id) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/conflicts/' + id)
                    .then((ans) => resolve(ans['Journeys'] || []), () => resolve([]));
            });
        },
        preview(actions, {id, contractId, lang}) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/preview/' + id, {contractId, lang})
                    .then((ans) => resolve(ans), (error) => reject(error.message));
            });
        },
        resolveForContract(actions, contractId) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/resolve/contract/' + contractId)
                    .then((ans) => resolve(ans), (error) => reject(error.message));
            });
        },

        // ---- responses ----
        getResponses(actions, {page, amount, filter, buids, sortby, sortcolumn} = {}) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/response/get', {
                    page: page || 1,
                    amount: amount || 25,
                    buids: (buids || actions.rootGetters['data/getSelectedBranchesIds']).join(","),
                    sortby: sortby || 'desc',
                    sortcolumn: sortcolumn || 'submittedAt'
                }, filter || {})
                    .then((ans) => resolve(ans), (error) => {
                        actions.dispatch('notifications/error', {
                            title: 'Journey responses',
                            message: (typeof error === 'object' && error.message !== undefined ? error.message : error)
                        }, {root: true});
                        reject(error.message);
                    });
            });
        },
        getResponse(actions, id) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/response/id/' + id)
                    .then((ans) => resolve(ans['Response']), (error) => reject(error.message));
            });
        },
        getResponsesByContract(actions, contractId) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/response/contract/' + contractId)
                    .then((ans) => resolve(ans['Responses'] || []), (error) => reject(error.message));
            });
        },
        /** Check-in progress of a reservation, derived from its journey. */
        getJourneyProgress(actions, contractId) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/progress/contract/' + contractId)
                    .then((ans) => resolve(ans['Progress']), (error) => reject(error.message));
            });
        },
        /** Mark one action step done/not done by hand. done: true | false | 'clear'. */
        setJourneyTaskOverride(actions, {contractId, questionKey, done}) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/progress/contract/' + contractId + '/task/'
                    + encodeURIComponent(questionKey) + '/' + done)
                    .then((ans) => resolve(ans['Progress']), (error) => reject(error.message));
            });
        },
        /** Staff switch: skip (or re-require) the journey for one reservation. */
        setSkipJourney(actions, {contractId, skip}) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/skip/' + contractId + '/' + (skip === true))
                    .then((ans) => resolve(ans['skipJourney'] === true), (error) => reject(error.message));
            });
        },
        markRead(actions, id) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/response/read/' + id)
                    .then((ans) => resolve(ans['Response']), (error) => reject(error.message));
            });
        },
        removeResponse({dispatch}, id) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/response/delete/' + id)
                    .then(
                        (ans) => {
                            dispatch('notifications/success',
                                {title: 'Journey', message: 'Response deleted'}, {root: true});
                            resolve(ans);
                        }, (error) => reject(error.message));
            });
        },
        getUnread({commit, rootGetters}) {
            return new Promise((resolve) => {
                api.sendRequest('journey/response/unread', {
                    buids: rootGetters['data/getSelectedBranchesIds'].join(",")
                }).then(
                    (ans) => {
                        commit('setUnread', ans['Total'] || 0);
                        resolve(ans['Total'] || 0);
                    }, () => resolve(0));
            });
        },
        getStats(actions, {journeyId, buids} = {}) {
            return new Promise((resolve, reject) => {
                api.sendRequest('journey/stats', {
                    journeyId: journeyId,
                    buids: (buids || actions.rootGetters['data/getSelectedBranchesIds']).join(",")
                }).then((ans) => resolve(ans), (error) => reject(error.message));
            });
        },
    }
}
